import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { useAuth } from "../../AuthContext";
import api from "../../api";

const HUB_URL = `${process.env.REACT_APP_API_URL.replace(/\/api\/?$/, "")}/hubs/support`;

const DashboardHeader = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);

  const connectionRef = useRef(null);

  const publicUrl = useMemo(() => process.env.REACT_APP_PUBLIC_URL || "/", []);

  const fetchSupportUnreadCount = async () => {
    try {
      const res = await api.get("/admin/support/conversations/open");
      const conversations = res.data.conversations || [];
      const total = conversations.reduce(
        (sum, c) => sum + (c.unreadCount || 0),
        0
      );
      setSupportUnreadCount(total);
    } catch (err) {
      console.error("Erreur unread support header :", err);
    }
  };

  useEffect(() => {
    fetchSupportUnreadCount();

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection.on("ConversationUpdated", () => {
      fetchSupportUnreadCount();
    });

    connection.start().catch((err) =>
      console.error("SignalR header error:", err)
    );

    connectionRef.current = connection;

    return () => {
      connection.stop();
      connectionRef.current = null;
    };
  }, []);

  const handleLogout = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir vous déconnecter ?")) return;
    try {
      setIsLoggingOut(true);
      await logout();
      navigate("/");
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isToolsActive =
    location.pathname.includes("/dashboard/newsletter") ||
    location.pathname.includes("/dashboard/chat");

  const navLinkClass = ({ isActive }) =>
    `px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition ${
      isActive
        ? "bg-blue-600 text-white shadow-md"
        : "text-slate-300 hover:bg-slate-800 hover:text-white"
    }`;

  const toolsTriggerClass = `relative inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold whitespace-nowrap transition ${
    isToolsActive
      ? "bg-cyan-600 border-cyan-500 text-white shadow-md"
      : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
  }`;

  return (
    <header className="sticky top-0 z-50 border-b border-slate-800 bg-slate-950/95 backdrop-blur">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

          <Link to="/dashboard" className="shrink-0 rounded-xl transition hover:opacity-90">
            <div className="flex items-center gap-3">
              <img
                src="/logo_front2.png"
                alt="LMFP"
                className="h-12 w-12 rounded-xl object-cover shadow-md"
              />
              <div>
                <h1 className="text-xl font-bold text-white">Dashboard</h1>
                <p className="text-sm text-slate-400">Administration LMFP</p>
              </div>
            </div>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            <nav className="flex flex-wrap items-center gap-2">

              <NavLink to="/dashboard" end className={navLinkClass}>Accueil</NavLink>
              <NavLink to="/dashboard/users" className={navLinkClass}>Users</NavLink>
              <NavLink to="/dashboard/comments" className={navLinkClass}>Commentaires</NavLink>
              <NavLink to="/dashboard/posts" className={navLinkClass}>Posts</NavLink>
              <NavLink to="/dashboard/galerie" className={navLinkClass}>Galerie</NavLink>
              <NavLink to="/dashboard/categories" className={navLinkClass}>Catégories</NavLink>

              <div className="mx-1 hidden h-6 w-px bg-slate-700 lg:block" />

              <div className="group relative">
                <button type="button" className={toolsTriggerClass}>
                  🧰 Outils
                  <span className="text-xs">▾</span>
                  {supportUnreadCount > 0 && (
                    <span className="absolute -right-2 -top-2 inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white shadow">
                      {supportUnreadCount}
                    </span>
                  )}
                </button>

                <div className="invisible absolute right-0 top-full z-50 mt-2 w-52 rounded-xl border border-slate-800 bg-slate-900 p-2 opacity-0 shadow-xl transition-all duration-150 group-hover:visible group-hover:opacity-100">
                  <NavLink
                    to="/dashboard/newsletter"
                    className={({ isActive }) =>
                      `block rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive
                          ? "bg-violet-600 text-white"
                          : "text-violet-200 hover:bg-violet-500/20 hover:text-white"
                      }`
                    }
                  >
                    📬 Newsletter
                  </NavLink>

                  <NavLink
                    to="/dashboard/chat"
                    className={({ isActive }) =>
                      `relative block rounded-lg px-3 py-2 text-sm font-medium transition ${
                        isActive
                          ? "bg-cyan-600 text-white"
                          : "text-cyan-200 hover:bg-cyan-500/20 hover:text-white"
                      }`
                    }
                  >
                    💬 Chat support
                    {supportUnreadCount > 0 && (
                      <span className="absolute right-2 top-1/2 inline-flex min-w-[22px] -translate-y-1/2 items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-bold text-white">
                        {supportUnreadCount}
                      </span>
                    )}
                  </NavLink>
                </div>
              </div>

            </nav>

            <div className="mx-1 hidden h-6 w-px bg-slate-700 lg:block" />

            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center justify-center rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold whitespace-nowrap text-slate-300 transition hover:bg-slate-800 hover:text-white"
            >
              Voir le site public
            </a>

            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="inline-flex shrink-0 items-center justify-center rounded-xl bg-red-500 px-4 py-2 text-sm font-semibold whitespace-nowrap text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:bg-red-300"
            >
              {isLoggingOut ? "Déconnexion..." : "Déconnexion"}
            </button>

          </div>

        </div>
      </div>
    </header>
  );
};

export default DashboardHeader;
